export type SubjectValueBindingStatus =
  | 'bound'
  | 'reference_missing'
  | 'processing'
  | 'unavailable'
  | 'pending'
  | 'missing';

export type SubjectValueBindingRow = {
  label: string;
  value: string;
};

export type SubjectValueBindingView = {
  status: SubjectValueBindingStatus;
  title: string;
  message: string;
  rows: SubjectValueBindingRow[];
  badgeLabel: string;
  shortText: string;
  badgeTone: 'default' | 'success' | 'warning';
};

export const SUBJECT_VALUE_UI_COPY = {
  bindingCardTitle: '学科画像映射',
  bindingExportTitle: '学科画像映射',
  moduleRequestCardTitle: '画像模块申请',
  moduleRequestCardSubtitle: '当当前画像里没有合适模块时，后台会自动提交新的画像模块补充申请。',
  moduleRequestExportTitle: '画像模块申请',
  shortText: {
    bound: '已绑定学科画像',
    referenceMissing: '学科画像引用已丢失',
    processing: '正在补齐学科画像映射',
    unavailable: '学科画像暂不可用',
    pending: '学科画像映射待补齐',
    missing: '尚未建立学科画像映射',
  },
  message: {
    processingTitle: '画像主干映射补齐中',
    processingBody: '后台正在补齐学科画像引用信息，请稍后重新打开详情。',
    missingTitle: '尚未建立画像主干映射',
    missingBody: '当前知识颗粒还没有可用的学科画像引用。',
    boundTitle: '已绑定学科画像主干',
    boundBody: '该知识颗粒当前已引用学科画像中的有效模块与主干节点。',
    referenceMissingTitle: '画像引用已丢失',
    referenceMissingBody: '学科画像中已无法解析这条主干节点引用，请重新补齐映射。',
    unavailableTitle: '画像引用暂不可用',
    unavailableBody: '当前无法获取可用的学科画像映射。',
    pendingTitle: '画像映射待补齐',
    pendingBody: '当前已有候选模块或节点，但尚未形成有效绑定。',
  },
} as const;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

/** 前台展示用：去掉内部服务名，避免对用户暴露 Runtime 等术语。 */
export function sanitizeUserFacingBindingText(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';
  return text
    .replace(/Runtime\s*Edge/gi, '学科画像服务')
    .replace(/\bRuntime\b/gi, '学科画像')
    .replace(/Runtime/gi, '学科画像')
    .replace(/加载学科画像\s*画像/g, '加载学科画像')
    .replace(/学科画像\s*学科画像/g, '学科画像')
    .replace(/等待学科画像审核/g, '等待后台审核')
    .replace(/提交学科画像审核/g, '提交后台审核')
    .trim();
}

export function getSubjectValueBindingRecord(metadata: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  const enrichment = asRecord(metadata?.revisionFamilyEnrichment);
  return asRecord(enrichment?.subjectValueBinding) || asRecord(metadata?.subjectValueBinding);
}

function readMetadataText(metadata: Record<string, unknown> | undefined, keys: string[]) {
  if (!metadata) {
    return '';
  }

  for (const key of keys) {
    const rawValue = metadata[key];
    if (typeof rawValue === 'string' && rawValue.trim()) {
      return rawValue.trim();
    }
  }

  return '';
}

export function hasSubjectValueBindingIssue(metadata: Record<string, unknown> | undefined): boolean {
  const binding = getSubjectValueBindingRecord(metadata);
  if (!binding) {
    return false;
  }

  const status = readMetadataText(binding, ['status']).toLowerCase();
  if (status === 'processing') {
    return false;
  }

  const hasReference = Boolean(readMetadataText(binding, [
    'subjectValueProfileId',
    'profileDisplayName',
    'moduleKey',
    'spineId',
    'spineNodeId',
  ]));

  if (!hasReference) {
    return false;
  }

  return !readMetadataText(binding, ['subjectCode']) || !readMetadataText(binding, ['subjectLabel']);
}

function formatPercent(value: unknown) {
  const numeric = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  if (!Number.isFinite(numeric)) {
    return '';
  }

  return `${Math.round(Math.max(0, Math.min(1, numeric)) * 100)}%`;
}

function readNumeric(value: unknown) {
  const numeric = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isFinite(numeric) ? numeric : Number.NaN;
}

function createView(input: Omit<SubjectValueBindingView, 'badgeLabel' | 'shortText' | 'badgeTone'>): SubjectValueBindingView {
  const mapping = {
    bound: {
      badgeLabel: '已绑定',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.bound,
      badgeTone: 'success' as const,
    },
    reference_missing: {
      badgeLabel: '引用缺失',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.referenceMissing,
      badgeTone: 'warning' as const,
    },
    processing: {
      badgeLabel: '处理中',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.processing,
      badgeTone: 'default' as const,
    },
    unavailable: {
      badgeLabel: '暂不可用',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.unavailable,
      badgeTone: 'warning' as const,
    },
    pending: {
      badgeLabel: '待补齐',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.pending,
      badgeTone: 'warning' as const,
    },
    missing: {
      badgeLabel: '未映射',
      shortText: SUBJECT_VALUE_UI_COPY.shortText.missing,
      badgeTone: 'warning' as const,
    },
  } satisfies Record<SubjectValueBindingStatus, Pick<SubjectValueBindingView, 'badgeLabel' | 'shortText' | 'badgeTone'>>;

  return {
    ...input,
    ...mapping[input.status],
  };
}

export function buildSubjectValueBindingView(metadata: Record<string, unknown> | undefined): SubjectValueBindingView {
  const enrichment = asRecord(metadata?.revisionFamilyEnrichment);
  const binding = asRecord(enrichment?.subjectValueBinding) || asRecord(metadata?.subjectValueBinding);
  const enrichmentStatus = readMetadataText(enrichment, ['status']);

  if (enrichmentStatus === 'processing' && !binding) {
    return createView({
      status: 'processing',
      title: SUBJECT_VALUE_UI_COPY.message.processingTitle,
      message: SUBJECT_VALUE_UI_COPY.message.processingBody,
      rows: [],
    });
  }

  if (!binding) {
    return createView({
      status: 'missing',
      title: SUBJECT_VALUE_UI_COPY.message.missingTitle,
      message: SUBJECT_VALUE_UI_COPY.message.missingBody,
      rows: [],
    });
  }

  const status = (readMetadataText(binding, ['status']) || 'pending') as SubjectValueBindingStatus;
  const subjectCode = readMetadataText(binding, ['subjectCode']);
  const subjectLabel = readMetadataText(binding, ['subjectLabel', 'candidateSubjectLabel']);
  const profileDisplayName = readMetadataText(binding, ['profileDisplayName']);
  const moduleLabel = readMetadataText(binding, ['moduleLabel', 'candidateModuleLabel']);
  const moduleKey = readMetadataText(binding, ['moduleKey']);
  const spineNodeTitle = readMetadataText(binding, ['spineNodeTitle', 'candidateSpineNodeTitle']);
  const reason = readMetadataText(binding, ['reason']);
  const numericConfidence = readNumeric(binding.confidence);
  const shouldHideLegacyConfidence = status !== 'bound' && Math.abs(numericConfidence - 0.35) < 0.001;
  const confidence = shouldHideLegacyConfidence ? '' : formatPercent(binding.confidence);

  const rows = [
    { label: '学科代码', value: subjectCode },
    { label: '学科名称', value: subjectLabel },
    { label: '画像', value: profileDisplayName },
    { label: '模块', value: moduleLabel || moduleKey },
    { label: '主干节点', value: spineNodeTitle },
    { label: '绑定来源', value: readMetadataText(binding, ['source']) },
    { label: '置信度', value: confidence },
  ].filter((item) => item.value);

  const secondary = asRecord(binding.secondary);
  if (secondary) {
    const secondaryTitle = readMetadataText(secondary, ['spineNodeTitle', 'candidateSpineNodeTitle', 'spineNodeId']);
    if (secondaryTitle) {
      rows.push({ label: '辅锚（预留）', value: secondaryTitle });
    }
  }

  if (status === 'bound') {
    return createView({
      status,
      title: SUBJECT_VALUE_UI_COPY.message.boundTitle,
      message: reason || SUBJECT_VALUE_UI_COPY.message.boundBody,
      rows,
    });
  }

  if (status === 'reference_missing') {
    return createView({
      status,
      title: '画像引用已丢失',
      message: reason || SUBJECT_VALUE_UI_COPY.message.referenceMissingBody,
      rows,
    });
  }

  if (status === 'unavailable') {
    return createView({
      status,
      title: SUBJECT_VALUE_UI_COPY.message.unavailableTitle,
      message: reason || SUBJECT_VALUE_UI_COPY.message.unavailableBody,
      rows,
    });
  }

  return createView({
    status: status === 'processing' ? 'processing' : 'pending',
    title: SUBJECT_VALUE_UI_COPY.message.pendingTitle,
    message: reason || SUBJECT_VALUE_UI_COPY.message.pendingBody,
    rows,
  });
}

export function resolveEnrichmentBackfillDegradedMessage(
  metadata: Record<string, unknown> | undefined,
): string | null {
  const enrichment = asRecord(metadata?.revisionFamilyEnrichment);
  if (!enrichment) {
    return null;
  }

  const status = readMetadataText(enrichment, ['status']).toLowerCase();
  if (status === 'failed') {
    return readMetadataText(enrichment, ['error']) || '重构补充信息失败';
  }

  const warnings = Array.isArray(enrichment.warnings)
    ? enrichment.warnings.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

  if (warnings.includes('runtime_spine_binding_pending_fast_exit')) {
    return readMetadataText(enrichment, ['error'])
      || '学科画像未就绪，本次未完成真实回填（仅保留待审核占位结果）。';
  }

  const moduleAttribution = asRecord(enrichment.subjectValueModuleAttribution);
  if (readMetadataText(moduleAttribution, ['status']).toLowerCase() === 'unavailable') {
    return '学科画像暂不可用，未完成补充信息回填。';
  }

  return null;
}

export function isKnowledgeParticleBackfillQueueResult(value: unknown): value is {
  particleId: string;
  status: string;
  queue?: Record<string, unknown>;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return typeof (value as { particleId?: unknown }).particleId === 'string'
    && typeof (value as { status?: unknown }).status === 'string'
    && !('metadata' in value);
}